import { doc, getDoc, setDoc, updateDoc, arrayUnion, Timestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Submit a suggestion - stores in user's contributions array
 */
export const submitSuggestion = async (
    rinkId,
    rinkName,
    type,
    currentValue,
    suggestedValue,
    userId,
    userName,
    userEmail,
    reason = ""
) => {
    try {
        const userDocRef = doc(db, 'user_contributions', userId);

        const contribution = {
            type, // 'location' or 'status_condition'
            rinkId,
            rinkName,
            currentValue,
            suggestedValue,
            reason,
            status: 'pending',
            timestamp: Timestamp.now(),
            reviewedAt: null,
            reviewedBy: null
        };

        // Check if user document exists
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            // Add contribution to array
            await updateDoc(userDocRef, {
                contributions: arrayUnion(contribution)
            });
        } else {
            // Create new user document
            await setDoc(userDocRef, {
                userId,
                userName: userName || 'Anonymous',
                userEmail: userEmail || '',
                votes: [],
                contributions: [contribution]
            });
        }

        return { success: true };
    } catch (error) {
        console.error('Error submitting suggestion:', error);
        throw error;
    }
};

/**
 * Get all pending suggestions from all users
 */
export const getPendingSuggestions = async () => {
    try {
        const userContributionsRef = collection(db, 'user_contributions');
        const snapshot = await getDocs(userContributionsRef);

        const pendingSuggestions = [];

        snapshot.forEach(doc => {
            const userData = doc.data();
            const contributions = userData.contributions || [];

            contributions.forEach((contribution, index) => {
                if (contribution.status === 'pending') {
                    pendingSuggestions.push({
                        id: `${doc.id}_${index}`, // Composite ID: userId_index
                        userId: doc.id,
                        contributionIndex: index,
                        userName: userData.userName || 'Anonymous',
                        ...contribution
                    });
                }
            });
        });

        return pendingSuggestions;
    } catch (error) {
        console.error('Error getting pending suggestions:', error);
        throw error;
    }
};

/**
 * Approve a suggestion - update aggregatedrinks and mark as approved
 */
export const approveSuggestion = async (userId, contributionIndex, adminId) => {
    try {
        // Get user document
        const userDocRef = doc(db, 'user_contributions', userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }

        const userData = userDoc.data();
        const contributions = userData.contributions || [];
        const contribution = contributions[contributionIndex];

        if (!contribution) {
            throw new Error('Contribution not found');
        }

        // Update contribution status
        contributions[contributionIndex] = {
            ...contribution,
            status: 'approved',
            reviewedAt: Timestamp.now(),
            reviewedBy: adminId
        };

        await updateDoc(userDocRef, {
            contributions
        });

        // Now update aggregatedrinks
        await applyToAggregateRinks(contribution);

        return { success: true };
    } catch (error) {
        console.error('Error approving suggestion:', error);
        throw error;
    }
};

/**
 * Reject a suggestion
 */
export const rejectSuggestion = async (userId, contributionIndex, adminId) => {
    try {
        const userDocRef = doc(db, 'user_contributions', userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }

        const userData = userDoc.data();
        const contributions = userData.contributions || [];

        contributions[contributionIndex] = {
            ...contributions[contributionIndex],
            status: 'rejected',
            reviewedAt: Timestamp.now(),
            reviewedBy: adminId
        };

        await updateDoc(userDocRef, {
            contributions
        });

        return { success: true };
    } catch (error) {
        console.error('Error rejecting suggestion:', error);
        throw error;
    }
};

/**
 * Apply approved suggestion to aggregatedrinks document
 */
const applyToAggregateRinks = async (contribution) => {
    try {
        const aggregateRef = doc(db, 'aggregatedrinks', 'data');
        const aggregateDoc = await getDoc(aggregateRef);

        if (!aggregateDoc.exists()) {
            throw new Error('Aggregate document not found');
        }

        let rinks = aggregateDoc.data().patinoire || [];
        const rinkIndex = rinks.findIndex(r => (r.id || r.nom) === contribution.rinkId);

        if (rinkIndex === -1) {
            throw new Error('Rink not found in aggregate');
        }

        const rink = rinks[rinkIndex];

        console.log('Applying contribution:', contribution);
        console.log('Contribution type:', contribution.type);
        console.log('Suggested value:', contribution.suggestedValue);

        if (contribution.type === 'location') {
            // Get the new location - handle both {location: {...}} and direct {...} formats
            const newLocation = contribution.suggestedValue.location || contribution.suggestedValue;

            console.log('New location to apply:', newLocation);

            // Save old location to history
            const locationHistory = rink.locationHistory || [];
            if (rink.location) {
                locationHistory.push({
                    lat: rink.location.lat,
                    lng: rink.location.lng,
                    timestamp: Timestamp.now(),
                    modifiedBy: 'admin'
                });
            }

            // Update location
            rinks[rinkIndex] = {
                ...rink,
                location: {
                    lat: newLocation.lat,
                    lng: newLocation.lng
                },
                locationHistory
            };

            console.log('Updated rink:', rinks[rinkIndex]);
        } else if (contribution.type === 'status_condition') {
            // Update status/condition
            rinks[rinkIndex] = {
                ...rink,
                ouvert: contribution.suggestedValue.status === 'Ouverte' ? 1 : 0,
                condition: contribution.suggestedValue.condition
            };
        }

        // Write back to Firestore
        await updateDoc(aggregateRef, {
            patinoire: rinks
        });

        return { success: true };
    } catch (error) {
        console.error('Error applying to aggregate rinks:', error);
        throw error;
    }
};

export const getUserContributions = async (userId) => {
    // Kept for compatibility - returns empty stats
    return {
        totalSuggestions: 0,
        approvedSuggestions: 0,
        totalVotes: 0
    };
};
